import { useState } from 'react';

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQSectionProps {
  title?: string;
  faqs: FAQItem[];
}

export default function FAQSection({ title = 'Frequently Asked Questions', faqs }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const half = Math.ceil(faqs.length / 2);
  const left = faqs.slice(0, half);
  const right = faqs.slice(half);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  const FAQItem = ({ item, index }: { item: FAQItem; index: number }) => (
    <div className="border-b border-warmgray last:border-0">
      <button
        onClick={() => toggle(index)}
        className="w-full text-left py-5 flex items-start justify-between gap-4 cursor-pointer group"
      >
        <span className="font-semibold text-charcoal text-base group-hover:text-bronze transition-colors leading-snug">
          {item.question}
        </span>
        <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
          <i
            className={`text-bronze transition-transform duration-200 ${
              openIndex === index ? 'ri-subtract-line' : 'ri-add-line'
            }`}
          ></i>
        </div>
      </button>
      {openIndex === index && (
        <div className="pb-5">
          <p className="text-gray-600 text-sm leading-relaxed">{item.answer}</p>
        </div>
      )}
    </div>
  );

  return (
    <section className="bg-ivory py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-bronze text-xs font-medium uppercase tracking-widest mb-3">Got Questions?</p>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-charcoal mb-3">{title}</h2>
          <div className="inline-flex items-center gap-2 bg-sage/10 text-sage text-xs font-medium px-4 py-2 rounded-full">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-mic-line text-sage text-sm"></i>
            </div>
            Voice Search Optimized Answers
          </div>
          <div className="w-16 h-0.5 bg-bronze mx-auto mt-6"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16">
          <div>{left.map((item, i) => <FAQItem key={i} item={item} index={i} />)}</div>
          <div>{right.map((item, i) => <FAQItem key={i + half} item={item} index={i + half} />)}</div>
        </div>
      </div>
    </section>
  );
}
